import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { format } from 'd3-format';
import * as d3 from 'd3-selection';
import { extent as arrayExtent, range } from 'd3-array';
import { radialLine } from 'd3-shape';
import { scaleOrdinal } from 'd3-scale';
import { timer } from 'd3-timer';

import widgetWrapper from './widgetWrapper';

import {
  chartLegend,
  colorScheme, formatTimestamp,
  getScaleType,
} from './widgetUtils';

import './PolarChartWidget.scss';

const ONE_HOUR_CYCLE = 1;
const TWELVE_HOUR_CYCLE = 12;
const TWENTY_FOUR_HOUR_CYCLE = 24;
const WEEK_CYCLE = 168;

const WEEK_DAYS = 'SUN MON TUE WED THU FRI SAT'.split(' ');

function createAngleAccessor(cycleHours) {
  switch (cycleHours) {
    case ONE_HOUR_CYCLE:
      return (d) => {
        const date = new Date(d[0]);
        return (2 * Math.PI)
          * ((date.getMinutes() + (date.getSeconds() / 60)) / 60);
      };
    case TWELVE_HOUR_CYCLE:
      return (d) => {
        const date = new Date(d[0]);
        let hrs = date.getHours();
        hrs = (hrs >= 12 ? hrs - 12 : hrs);
        return (2 * Math.PI)
          * ((hrs + (date.getMinutes() / 60) + (date.getSeconds() / 3600)) / 12);
      };
    case TWENTY_FOUR_HOUR_CYCLE:
      return (d) => {
        const date = new Date(d[0]);
        return (2 * Math.PI)
          * ((date.getHours() + (date.getMinutes() / 60) + (date.getSeconds() / 3600)) / 24);
      };
    case WEEK_CYCLE:
      return (d) => {
        const date = new Date(d[0]);
        return (2 * Math.PI)
          * ((date.getDay() + (date.getHours() / 24) + (date.getMinutes() / 1440)) / 7);
      };
    default:
      return d => d;
  }
}

function angularLabelData(cycleHours) {
  if (cycleHours === ONE_HOUR_CYCLE) {
    const mins = range(0, 60, 5);
    mins[0] = 60;
    return mins;
  }
  if (cycleHours === TWELVE_HOUR_CYCLE) {
    const hrs = range(0, 12, 1);
    hrs[0] = 12;
    return hrs;
  }
  if (cycleHours === TWENTY_FOUR_HOUR_CYCLE) {
    const hrs = range(0, 24, 1);
    hrs[0] = 24;
    return hrs;
  }
  if (cycleHours === WEEK_CYCLE) {
    return WEEK_DAYS;
  }
  return [];
}

const findEarliestTime = pathData => pathData.reduce(
  (earliest, array) => Math.min(earliest, array[0][0] ? array[0][0] : earliest),
  Number.POSITIVE_INFINITY,
);

const findLatestTime = pathData => pathData.reduce((latest, array) => Math.max(
  latest,
  array[array.length - 1] && array[array.length - 1][0]
    ? array[array.length - 1][0]
    : latest,
), 0);

function segmentPaths(pathData, cycleHours, indexLimit = Number.POSITIVE_INFINITY) {
  const exclusiveIndexLimit = pathData.reduce(
    (acc, val) => Math.min(acc, val.length),
    Math.ceil(indexLimit),
  );
  if (exclusiveIndexLimit === 0
    || (!([
      ONE_HOUR_CYCLE,
      TWELVE_HOUR_CYCLE,
      TWENTY_FOUR_HOUR_CYCLE,
      WEEK_CYCLE,
    ].includes(cycleHours)))) {
    return new Array(pathData.length).fill([]);
  }
  const truncatedPathData = pathData.map(e => e.slice(0, exclusiveIndexLimit));
  const earliestTimestamp = findEarliestTime(truncatedPathData);
  const latestTimestamp = findLatestTime(truncatedPathData);
  let cycleLengthMilliseconds = 3600000;
  if (cycleHours === TWELVE_HOUR_CYCLE) {
    cycleLengthMilliseconds *= 12;
  }
  if (cycleHours === TWENTY_FOUR_HOUR_CYCLE) {
    cycleLengthMilliseconds *= 24;
  }
  if (cycleHours === WEEK_CYCLE) {
    cycleLengthMilliseconds *= 168;
  }
  cycleLengthMilliseconds /= 12; // Do a dozen segments per cycle
  let result = [];
  for (let ts = latestTimestamp - cycleLengthMilliseconds;
    ts >= earliestTimestamp - cycleLengthMilliseconds;
    ts -= cycleLengthMilliseconds) {
    const buckets = [];
    truncatedPathData.forEach((array) => {
      buckets.push(array.filter(e => ((e[0] >= ts) && (e[0] <= ts + cycleLengthMilliseconds))));
    });
    result = buckets.concat(result);
  }
  return result;
}

function centerTimeLabels(timestamp) {
  const d = new Date(timestamp);
  let hrs = d.getHours();
  let ampm = 'AM';
  if (hrs >= 12) {
    hrs -= 12;
    ampm = 'PM';
  }
  if (hrs === 0) {
    hrs = 12;
  }
  const weekday = WEEK_DAYS[d.getDay()];
  return [
    {
      text: weekday,
      textAnchor: 'middle',
      x: 0,
      y: -17,
    },
    {
      text: hrs,
      textAnchor: 'end',
      x: -7,
      y: 0,
    },
    {
      text: ':',
      textAnchor: 'middle',
      x: -4,
      y: 0,
    },
    {
      text: d.getMinutes().toString().padStart(2, '0'),
      textAnchor: 'start',
      x: 0,
      y: 0,
    },
    {
      text: ampm,
      textAnchor: 'middle',
      x: 0,
      y: 23,
    },
  ];
}

function getTiltAngle(cycleProps, radialAxisProps) {
  if (radialAxisProps.tiltAngle !== 'auto') {
    return radialAxisProps.tiltAngle;
  }
  if (cycleProps.hours === 24) {
    return 8;
  }
  return 15;
}

class PolarChartWidget extends Component {

  static propTypes = {
    series: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.number.isRequired })),
      keys: PropTypes.arrayOf(PropTypes.string),
      axis: PropTypes.shape({
        type: PropTypes.string,
        hours: PropTypes.number,
        tickLength: PropTypes.number,
        tickPadding: PropTypes.number,
        animate: PropTypes.bool,
        animationDuration: PropTypes.number,
        animationRepeat: PropTypes.number,
      }),
      radialAxis: PropTypes.shape({
        type: PropTypes.string,
        hours: PropTypes.number,
        tickLength: PropTypes.number,
        tickPadding: PropTypes.number,
        tickFormat: null,
        tickCount: PropTypes.number,
        tiltAngle: PropTypes.string,
      }),
      margin: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        left: PropTypes.number,
        bottom: PropTypes.number,
      }),
      colors: PropTypes.arrayOf(PropTypes.string),
    }),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
  };

  static defaultProps = {
    title: '',
    subtitle: '',
    timestamp: '',
    className: '',
    series: {},
  };

  constructor(...args) {
    super(...args);
    this.outerRef = React.createRef();
    this.legendRef = React.createRef();
    this.renderID = 0;
    this.establishedFrameTime = 0; // max time between frames on starting loop
  }

  componentDidMount() {
    window.addEventListener('resize', this.doD3Rendering);
    this.doD3Rendering();
  }

  componentDidUpdate(_prevProps, _prevState) {
    this.doD3Rendering();
  }

  doD3Rendering() {
    if (this.props.series) {
      const series = this.props.series;

      if (!series || !series.data || series.data.length === 0 || !series.keys) {
        return;
      }

      series.data.sort((a, b) => a.key - b.key);

      const {
        data: dataArray = [],
        margin: marginProperty = {},
      } = series;

      let { keys = ['value'] } = series;

      const marginProps = Object.assign({
        top: 10,
        right: 10,
        left: 10,
        bottom: 10,
      }, marginProperty);

      const cycleProps = Object.assign({
        hours: 12,
        tickLength: 10,
        tickPadding: 20,
        animate: false,
        animationDuration: 15000,
        animationRepeat: 30000,
      }, series.cycle);

      const radialAxisProps = Object.assign({
        type: 'linear',
        tickPadding: 3,
        tickFormat: null,
        tickCount: 5,
        tiltAngle: 'auto',
      }, series.radialAxis);

      if (typeof radialAxisProps.tickFormat === 'string') {
        radialAxisProps.tickFormat = format(radialAxisProps.tickFormat);
      }
      const padding = series.padding || 30;

      // Persist the color scale.
      const scheme = series.colors instanceof Array
        ? series.colors
        : colorScheme;
      const colorScale = this.colorScale
        || (this.colorScale = scaleOrdinal().range(scheme));
      // Initialize accessors for various properties.
      const angleAccessor = createAngleAccessor(cycleProps.hours);
      const colorAccessor = (k => colorScale(k));

      let svg = d3.select(this.outerRef.current)
        .selectAll('.polar-chart')
        .data([0]);
      svg = svg
        .enter()
        .append('svg')
        .attr('class', 'polar-chart')
        .merge(svg);

      let pathData = keys.map(key => dataArray.map(e => ([e.key, e[key]])));

      // Examine data for domains.
      let extent;
      const radialDomain = series.radialAxis.radialDomain || pathData.reduce((e, s) => {
        extent = arrayExtent(s, d => d[1]);
        if (e[0] === undefined || extent[0] < e[0]) { e[0] = extent[0]; }
        if (e[1] === undefined || extent[1] > e[1]) { e[1] = extent[1]; }
        if (series.radialAxis.type === 'log') {
          e[0] = Math.max(e[0], 1.0);
          e[1] = Math.max(e[1], 10 * e[0]);
        }
        return e;
      }, []);

      // At this point we may need to remove stuff with no data points within radialDomain.
      for (let j = 0; j < keys.length; j += 1) {
        const extentForKey = arrayExtent(pathData[j], d => d[1]);
        if (extentForKey[0] > radialDomain[1] || extentForKey[1] < radialDomain[0]) {
          keys[j] = null;
          pathData[j] = null;
        }
      }
      keys = keys.filter(e => e !== null);
      pathData = pathData.filter(e => e !== null);

      // Create the legend. The legend is rendered using HTML for better layout
      // support and before the SVG so that the space available to the SVG is
      // limited by the legend's size.
      const legend = d3.select(this.outerRef.current)
        .selectAll('.legend')
        .data([keys.length > 1 ? keys : []]);
      legend.enter()
        .append('ul')
        .attr('class', 'legend')
        .merge(legend)
        .call(chartLegend, colorAccessor);

      // Set the SVG dimensions.
      const width = svg.node().parentNode.clientWidth;
      const height = svg.node().parentNode.clientHeight;
      const chartWidth = width - marginProps.left - marginProps.right;
      const chartHeight = height - marginProps.top - marginProps.bottom;
      svg
        .attr('width', width)
        .attr('height', height);
      let margin = svg.selectAll('.margin')
        .data([]);
      margin.exit().remove();
      margin = svg.selectAll('.margin')
        .data([this.renderID]);
      margin = margin.enter()
        .append('g')
        .attr('class', 'margin')
        .merge(margin)
        .attr('transform', `translate(${marginProps.left + (chartWidth / 2)}, ${marginProps.top + (chartHeight / 2)})`);

      const outerRadius = Math.min(chartWidth, (chartHeight) / 2) - padding;
      const innerRadius = 40;

      const radialScale = (getScaleType(radialAxisProps.type))()
        .domain(radialDomain)
        .range([innerRadius, outerRadius])
        .nice();

      const line = radialLine()
        .radius(d => radialScale(d[1]))
        .angle(angleAccessor)
        .defined(d => Number.isFinite(d[0])
          && Number.isFinite(d[1])
          && d[1] >= radialDomain[0]
          && d[1] <= radialDomain[1]);

      const gr = margin.append('g')
        .attr('class', 'r radial-axis')
        .selectAll('g')
        .data(radialScale.ticks(radialAxisProps.tickCount))
        .enter()
        .append('g');

      gr.append('circle')
        .attr('r', radialScale);

      gr.append('text')
        .attr('y', d => -radialScale(d) - radialAxisProps.tickPadding)
        .attr('transform', `rotate(${getTiltAngle(cycleProps, radialAxisProps)})`)
        .style('text-anchor', 'middle')
        .text(radialAxisProps.tickFormat ? radialAxisProps.tickFormat : d => d);

      const angularLabels = angularLabelData(cycleProps.hours);

      const offsetForWeekdayLabels = (cycleProps.hours === 168 ? (2 * Math.PI) / 14 : 0);

      // Outer labels
      margin.append('g')
        .attr('class', 'a axis')
        .selectAll('text')
        .data(angularLabels)
        .enter()
        .append('text')
        .style('text-anchor', 'middle')
        .style('alignment-baseline', 'central')
        .attr('x', (d, i) => ((outerRadius + cycleProps.tickPadding)
          * Math.sin(offsetForWeekdayLabels + (2 * Math.PI * (i / angularLabels.length)))))
        .attr('y', (d, i) => -((outerRadius + cycleProps.tickPadding)
          * Math.cos(offsetForWeekdayLabels + (2 * Math.PI * (i / angularLabels.length)))))
        .text(d => d);

      margin.append('g')
        .attr('class', 'axis')
        .selectAll('line')
        .data(angularLabels)
        .enter()
        .append('line')
        .attr('x1', (d, i) => (innerRadius * Math.sin(2 * Math.PI * (i / angularLabels.length))))
        .attr('x2', (d, i) => ((outerRadius + cycleProps.tickLength) * Math.sin(2 * Math.PI * (i / angularLabels.length))))
        .attr('y1', (d, i) => -(innerRadius * Math.cos(2 * Math.PI * (i / angularLabels.length))))
        .attr('y2', (d, i) => -((outerRadius + cycleProps.tickLength) * Math.cos(2 * Math.PI * (i / angularLabels.length))));

      const sweepLine = margin.append('g')
        .attr('class', 'sweep-line')
        .selectAll('line')
        .data([this.renderID]);
      sweepLine.exit().remove();
      sweepLine.enter().append('line')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', -innerRadius)
        .attr('y2', -outerRadius);

      const g = margin.selectAll('.seriesgroup').data([this.renderID]);
      g.exit().remove();
      g.enter()
        .append('g')
        .attr('class', 'seriesgroup');

      const numKeys = keys.length;

      // These two needed some fine-tuning to fade out lines attractively
      const strokeWidths = range(0, -40, -1).map(e => 3.0 * (1.02 ** e));
      const strokeOpacities = range(0.5, 1.0, 0.02).reverse();

      const draw = (fractionComplete = 1.0) => {
        const segmentedPaths = segmentPaths(
          pathData,
          cycleProps.hours,
          Math.ceil(pathData[0].length * fractionComplete),
        );
        const segmentsPerKey = segmentedPaths.length / numKeys;
        const sg = margin.selectAll('.seriesgroup').data([this.renderID]);
        sg.exit().remove();
        sg.enter()
          .append('g')
          .attr('class', 'seriesgroup');
        const ser = sg.selectAll('.series').data(segmentedPaths);
        ser.exit().remove();
        ser.enter()
          .append('path')
          .merge(ser)
          .attr('class', 'series')
          .attr('d', line)
          .style('stroke', (d, i) => colorAccessor(keys[i % numKeys], i))
          .style('stroke-width', (d, i) => {
            const value = segmentsPerKey - ((i - (i % numKeys)) / numKeys) - 1;
            const strokeWidth = value < strokeWidths.length
              ? strokeWidths[value]
              : strokeWidths[strokeWidths.length - 1];
            return `${strokeWidth}px`;
          })
          .style('stroke-opacity', (d, i) => {
            const value = segmentsPerKey - ((i - (i % numKeys)) / numKeys) - 1;
            const strokeOpacity = value < strokeOpacities.length
              ? strokeOpacities[value]
              : strokeOpacities[strokeOpacities.length - 1];
            return strokeOpacity.toString();
          });
        const currentTimestamp = findLatestTime(segmentedPaths);
        if (currentTimestamp) {
          margin.selectAll('.sweep-line').attr('transform', `rotate(${(180 * angleAccessor([currentTimestamp])) / Math.PI})`);
          const hub = margin.selectAll('.hub')
            .data([currentTimestamp]);
          hub.exit().remove();
          hub.enter()
            .append('circle')
            .merge(hub)
            .attr('class', 'hub')
            .attr('cx', 0)
            .attr('cy', 0)
            .attr('r', innerRadius);
          const hubtext = margin.selectAll('.hubtext')
            .data(centerTimeLabels(currentTimestamp));
          hubtext.exit().remove();
          hubtext.enter()
            .append('text')
            .merge(hubtext)
            .attr('class', 'hubtext')
            .text(d => d.text)
            .attr('text-anchor', d => d.textAnchor)
            .attr('x', d => d.x)
            .attr('y', d => d.y);
        }
      };

      if (cycleProps.animate && (!(typeof window.process === 'object' && window.process.type === 'renderer'))) {
        const myID = this.renderID;
        const runAnimation = () => {
          if (myID !== this.renderID) {
            return;
          }
          const firstRun = (myID === this.renderID && this.establishedFrameTime === 0);
          let lastElapsed = null;
          let lastFrameTime = null;
          const animationTimer = timer((elapsed) => {
            if (myID !== this.renderID) {
              animationTimer.stop();
              return;
            }
            if (lastElapsed !== null) {
              lastFrameTime = elapsed - lastElapsed;
            }
            if (firstRun) {
              this.establishedFrameTime = Math.max(lastFrameTime, this.establishedFrameTime);
            } else if (elapsed - lastElapsed < this.establishedFrameTime) {
              return;
            }
            draw(elapsed / cycleProps.animationDuration);
            lastElapsed = elapsed;
            if (elapsed > cycleProps.animationDuration) {
              animationTimer.stop();
            }
          });
          setTimeout(runAnimation, cycleProps.animationRepeat);
        };
        runAnimation();
      } else {
        draw();
      }
    }
  }

  render() {
    const {
      title,
      subtitle,
      timestamp,
      className,
    } = this.props;
    return (
      <div ref={this.outerRef} className={`PolarChartWidget ${className}`}>
        <div className="header">
          <div className="title">{title}</div>
          <div className="subtitle">{subtitle}</div>
        </div>
        <div className="content">
          <svg className="polar-chart" />
        </div>
        <div className="footer">
          <ul className="legend" ref={this.legendRef} />
          <div className="timestamp">{formatTimestamp(timestamp)}</div>
        </div>
      </div>
    );
  }
}

export default widgetWrapper(PolarChartWidget);
